import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Copy, RefreshCw, UserPlus, FileSpreadsheet, ExternalLink, Loader2, Settings2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useAuth } from '../context/AuthContext';
import { useRegion, getRegionLabel } from '../context/RegionContext';
import AttemptsTable from '../components/assessment/AttemptsTable';
import InvitesTable from '../components/assessment/InvitesTable';
import InviteModal from '../components/assessment/InviteModal';
import ConfigPanel from '../components/assessment/ConfigPanel';
import {
  getAssessmentTest, getAssessmentAttempts, getAssessmentInvites, updateAssessmentPublicLink, assessmentTakeUrl, apiError, pickRegionLink,
} from '../services/assessmentApi';

const RegenerateDialog = ({ open, onOpenChange, onConfirm }) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent data-testid="regenerate-link-dialog">
      <AlertDialogHeader>
        <AlertDialogTitle>Regenerate the public link?</AlertDialogTitle>
        <AlertDialogDescription>
          The current link stops working immediately for everyone you have shared it with. Candidates already taking the test are not affected.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="regenerate-cancel-btn">Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} data-testid="regenerate-confirm-btn">Regenerate</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const PublicLinkCard = ({ test, onUpdate, canManage }) => {
  const { region: selected } = useRegion();
  const [region, link] = pickRegionLink(test.public_links, selected);
  const url = assessmentTakeUrl(link.token);
  const [confirm, setConfirm] = useState(false);
  const update = async (body) => {
    try { onUpdate(region, await updateAssessmentPublicLink(test.id, { ...body, region })); toast.success('Public link updated'); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="rounded-2xl border bg-card p-5 space-y-3" data-testid="public-link-card">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold flex items-center gap-2">Public test link
            {region && <span className="text-[10px] font-semibold uppercase tracking-wider bg-primary/10 text-primary px-2 py-0.5 rounded-full" data-testid="public-link-region">{getRegionLabel(region)}</span>}
          </p>
          <p className="text-xs text-muted-foreground">Each region has its own link — results from this link are only visible to the {getRegionLabel(region)} team.</p>
        </div>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          {link.enabled ? 'Enabled' : 'Disabled'}
          <Switch checked={!!link.enabled} disabled={!canManage} onCheckedChange={(v) => update({ enabled: v })} data-testid="public-link-toggle" />
        </label>
      </div>
      <div className="flex gap-2">
        <Input readOnly value={url} className="font-mono text-xs" data-testid="public-link-input" />
        <Button variant="outline" className="gap-1.5" onClick={() => { navigator.clipboard.writeText(url).catch(() => {}); toast.success('Public link copied'); }} data-testid="copy-public-test-link-btn"><Copy className="h-4 w-4" /> Copy</Button>
        <Button variant="outline" size="icon" asChild><a href={url} target="_blank" rel="noreferrer" data-testid="open-public-link-btn"><ExternalLink className="h-4 w-4" /></a></Button>
        {canManage && <Button variant="ghost" size="icon" title="Regenerate link" onClick={() => setConfirm(true)} data-testid="regenerate-public-link-btn"><RefreshCw className="h-4 w-4" /></Button>}
      </div>
      <RegenerateDialog open={confirm} onOpenChange={setConfirm} onConfirm={() => { setConfirm(false); update({ regenerate: true }); }} />
    </div>
  );
};

export default function AssessmentTestPage() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [invites, setInvites] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const { user } = useAuth();
  const canManage = ['admin', 'recruiter'].includes(user?.role);
  const isAdmin = user?.role === 'admin';
  const seq = useRef(0);

  const load = useCallback(async () => {
    const my = ++seq.current;
    try {
      const [t, a, i] = await Promise.all([getAssessmentTest(testId), getAssessmentAttempts(testId), getAssessmentInvites(testId)]);
      if (my !== seq.current) return; // a newer load or link update superseded this response
      setTest(t); setAttempts(a.attempts); setInvites(i.invites);
    } catch (e) { toast.error(apiError(e)); }
  }, [testId]);
  useEffect(() => { load(); const id = setInterval(load, 20000); return () => clearInterval(id); }, [load]);

  if (!test) return <div className="flex justify-center py-24"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;
  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto space-y-6" data-testid="assessment-test-page">
      <Button variant="ghost" className="gap-2 -ml-2" onClick={() => navigate('/assessments')} data-testid="back-to-library-btn"><ArrowLeft className="h-4 w-4" /> Library</Button>
      <div className="grid lg:grid-cols-[1fr_1.1fr] gap-6 items-start">
        <div className="flex items-start gap-4">
          <span className="w-14 h-14 rounded-2xl bg-[#107C41] text-white flex items-center justify-center shrink-0"><FileSpreadsheet className="h-7 w-7" /></span>
          <div>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{test.title}</h1>
            <p className="text-sm text-muted-foreground mt-1">{test.duration_minutes} min · {test.min_skills}–{test.max_skills} self-selected skills · {test.stats.completed} completed · avg {test.stats.avg_score ?? '—'}%</p>
            {canManage && <Button className="mt-4 gap-2" onClick={() => setInviteOpen(true)} data-testid="create-candidate-invite-btn"><UserPlus className="h-4 w-4" /> Create personal link</Button>}
          </div>
        </div>
        <PublicLinkCard test={test} canManage={canManage} onUpdate={(r, link) => { seq.current += 1; setTest((t) => ({ ...t, public_links: { ...t.public_links, [r]: link } })); }} />
      </div>
      <Tabs defaultValue="results">
        <TabsList>
          <TabsTrigger value="results" data-testid="tab-results">Results ({attempts.length})</TabsTrigger>
          <TabsTrigger value="invites" data-testid="tab-invites">Personal links ({invites.length})</TabsTrigger>
          <TabsTrigger value="about" data-testid="tab-about">What's tested</TabsTrigger>
          {isAdmin && <TabsTrigger value="config" className="gap-1.5" data-testid="tab-config"><Settings2 className="h-3.5 w-3.5" /> Config</TabsTrigger>}
        </TabsList>
        <TabsContent value="results" className="mt-4"><AttemptsTable attempts={attempts} /></TabsContent>
        <TabsContent value="invites" className="mt-4"><InvitesTable invites={invites} onChange={load} canManage={canManage} defaultMin={test.min_skills} /></TabsContent>
        <TabsContent value="about" className="mt-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="tested-skills-grid">
            {test.skills.map((s) => (
              <div key={s.id} className="rounded-xl border bg-card p-4">
                <p className="text-sm font-semibold">{s.name}</p>
                <p className="text-xs text-muted-foreground mt-1">{s.description}</p>
                <span className={`mt-2 inline-block text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded ${s.mode === 'hands_on' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-amber-500/10 text-amber-600'}`}>
                  {s.mode === 'hands_on' ? 'Live spreadsheet + scenario' : 'Scenario questions'}
                </span>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-4 max-w-3xl">
            Scoring: each spreadsheet task is graded on the correct result (70%), using a real formula instead of a typed value (15%) and the expected technique (15%).
            Rules (conditional formatting, data validation) are checked for the right range and settings. Skill score = weighted average; improvement gap = 100 − score.
          </p>
        </TabsContent>
        {isAdmin && <TabsContent value="config" className="mt-4"><ConfigPanel test={test} onSaved={load} /></TabsContent>}
      </Tabs>
      <InviteModal open={inviteOpen} onOpenChange={setInviteOpen} testId={testId} onCreated={load} defaultMin={test.min_skills} maxSkills={test.max_skills} baseMin={test.base_min_skills} />
    </div>
  );
}
